import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getYearProgress, getProgressMessage } from '../utils/yearProgress';
import { useTranslation } from '../i18n';
import './YearProgress.scss';

/**
 * Year progress indicator showing a pie chart with contextual messages on hover.
 * Only interactive for the current year; shows static 0%/100% for future/past years.
 * Scales automatically with parent container (sticky corner) zoom.
 * 
 * @param {Object} props
 * @param {number} props.year - The year to display progress for
 */
function YearProgress({ year }) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [message, setMessage] = useState('');
  const containerRef = useRef(null);

  // Calculate progress data
  const progress = useMemo(() => getYearProgress(year), [year]);
  const { percent, dayOfYear, daysRemaining, daysInYear, isCurrentYear } = progress;

  // Format the description (facts section) for tooltip
  const tooltipDescription = useMemo(() => {
    if (!isCurrentYear) return null;
    return t('yearProgress.tooltip', { 
      day: dayOfYear, 
      daysInYear, 
      percent, 
      remaining: daysRemaining 
    });
  }, [isCurrentYear, dayOfYear, daysInYear, percent, daysRemaining, t]);

  // Handle mouse enter - randomize message and position tooltip
  const handleMouseEnter = useCallback(() => {
    if (!isCurrentYear) return;

    // Get random message for current progress (pass t function for translations)
    const newMessage = getProgressMessage(progress, t);
    setMessage(newMessage);

    // Position tooltip below the corner, with arrow pointing to pie chart center
    const rect = containerRef.current.getBoundingClientRect();
    const pieChartCenterX = rect.left + rect.width / 2; // Center of the pie chart
    const topY = rect.bottom + 8; // 8px gap below the chart
    
    // Tooltip starts at left edge of corner, arrow will be offset to point at center
    setTooltipPos({ x: rect.left, y: topY, arrowOffset: pieChartCenterX - rect.left });
    setIsHovered(true);
  }, [isCurrentYear, progress, t]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Forward wheel events to calendar container for zoom functionality
  // This allows both tooltip hover and zoom to work simultaneously
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      // Find the calendar container (scrollable element with zoom handler)
      const calendarContainer = document.querySelector('.calendar__container');
      if (!calendarContainer) return;

      // Create a new wheel event with the same properties
      const forwardedEvent = new WheelEvent('wheel', {
        deltaY: e.deltaY,
        deltaX: e.deltaX,
        deltaMode: e.deltaMode,
        clientX: e.clientX,
        clientY: e.clientY,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        bubbles: true,
        cancelable: true,
      });

      // Dispatch to calendar container so zoom handler catches it
      calendarContainer.dispatchEvent(forwardedEvent);

      // Prevent default to avoid double-scrolling
      e.preventDefault();
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // Calculate stroke-dasharray for pie chart
  // Circumference = 2πr, where r = 14.96 gives us approximately 94 units
  const circumference = 94;
  const strokeDasharray = `${(percent / 100) * circumference}, ${circumference}`;

  return (
    <div 
      className={`year-progress ${!isCurrentYear ? 'year-progress--inactive' : ''}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <svg 
        className="year-progress__chart" 
        viewBox="0 0 36 36"
        aria-label={t('yearProgress.ariaLabel', { percent, year })}
      >
        {/* Background circle (remaining portion) */}
        <circle
          className="year-progress__bg"
          cx="18"
          cy="18"
          r="14.96"
          fill="transparent"
          strokeWidth="10"
        />
        
        {/* Progress arc (elapsed portion) */}
        <circle
          className="year-progress__arc"
          cx="18"
          cy="18"
          r="14.96"
          fill="transparent"
          strokeWidth="10"
          strokeDasharray={strokeDasharray}
          strokeDashoffset="23.5"
          strokeLinecap="round"
        />
      </svg>

      {/* Custom tooltip positioned below (instead of standard Tooltip component) */}
      {isCurrentYear && isHovered && createPortal(
        <div 
          className="year-progress__tooltip"
          style={{ 
            left: tooltipPos.x, 
            top: tooltipPos.y,
            '--arrow-offset': `${tooltipPos.arrowOffset}px`
          }}
        >
          <div className="year-progress__tooltip-title">{message}</div>
          <div className="year-progress__tooltip-description">{tooltipDescription}</div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default YearProgress;
